import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { MODULES, can, normRole, ROLE_LABELS, ROLE_SCOPE } from "@/lib/permissions";
import {
  LayoutDashboard, FileText, FolderOpen, Users, BarChart3, ShieldCheck,
  UserCog, Map, ClipboardList, IndianRupee, Settings, ArrowRight, Lock, Clock, CheckCircle2,
} from "lucide-react";

const ICONS = {
  LayoutDashboard, FileText, FolderOpen, Users, BarChart3, ShieldCheck,
  UserCog, Map, ClipboardList, IndianRupee, Settings,
};

export default function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const role = normRole(user?.role);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const openModule = (m) => {
    if (!m.built) { toast.message(`${m.label} — coming soon`, { description: "This module is on the roadmap." }); return; }
    navigate(m.path);
  };

  const visible = MODULES.filter((m) => can(role, m.key, "view"));

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        {/* Welcome */}
        <div className="rounded-3xl bg-emerald-950 text-white p-6 sm:p-8 grain relative overflow-hidden" data-testid="staff-welcome">
          <div className="relative z-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-3.5 py-1 text-xs font-semibold text-emerald-200" data-testid="staff-role-badge">
              <ShieldCheck className="h-3.5 w-3.5" /> {ROLE_LABELS[role] || "Staff"}
            </span>
            <h1 className="mt-3 font-head font-extrabold text-2xl sm:text-3xl">Welcome back, {user?.name || user?.email}</h1>
            <p className="mt-1.5 text-emerald-100/70 text-sm">{ROLE_SCOPE[role] || "Role-based access"} · You can access {visible.length} module{visible.length === 1 ? "" : "s"}.</p>
          </div>
        </div>

        {/* Quick stats (if reports/dashboard visible) */}
        {can(role, "reports", "view") && (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Users} tint="#eaf5ee" color="#1f6b3b" label="Total Applications" value={stats?.total ?? "—"} testid="staff-stat-total" />
            <Stat icon={Clock} tint="#fef3c7" color="#b45309" label="Pending Review" value={stats?.pending ?? "—"} testid="staff-stat-pending" />
            <Stat icon={CheckCircle2} tint="#dcfce7" color="#15803d" label="Sanctioned" value={stats?.sanctioned_count ?? "—"} testid="staff-stat-sanctioned" />
            <Stat icon={IndianRupee} tint="#e6f0fb" color="#2563eb" label="Sanctioned Value" value={stats ? `₹ ${Number(stats.sanctioned_amount || 0).toLocaleString("en-IN")}` : "—"} testid="staff-stat-amount" />
          </div>
        )}

        {/* Modules */}
        <h2 className="mt-9 mb-4 font-head font-bold text-lg text-emerald-950">Your Modules</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="staff-modules-grid">
          {visible.map((m) => {
            const Icon = ICONS[m.icon] || LayoutDashboard;
            const level = can(role, m.key, "full") ? "Full access" : can(role, m.key, "manage") ? "Manage" : "View only";
            return (
              <button
                key={m.key}
                onClick={() => openModule(m)}
                data-testid={`module-${m.key}`}
                className={`text-left bg-white rounded-3xl p-6 border transition-all group ${m.built ? "border-emerald-100 hover:shadow-lg hover:-translate-y-0.5 hover:border-emerald-300" : "border-dashed border-slate-200 opacity-80"}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-2xl grid place-items-center bg-emerald-50"><Icon className="h-6 w-6 text-emerald-700" /></div>
                  {m.built ? (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{level}</span>
                  ) : (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-400 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Soon</span>
                  )}
                </div>
                <h3 className="mt-4 font-head font-bold text-emerald-950">{m.label}</h3>
                <p className="mt-1 text-sm text-slate-500">{m.desc}</p>
                {m.built && <span className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm">Open <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tint, color, testid }) => (
  <Card className="rounded-2xl border-emerald-100 p-5" data-testid={testid}>
    <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ backgroundColor: tint }}><Icon className="h-5 w-5" style={{ color }} /></div>
    <div className="mt-3 font-head font-extrabold text-2xl text-emerald-950">{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </Card>
);
